/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const STORAGE_KEY = 'kahu-window-state';
export const KAHU_WINDOW_STATE_EVENT = 'kahu-window-state-change';

type KahuWindowState = {
  isDocked: boolean;
  size: { width: number; height: number };
};

const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 438;

const getInitialState = (): KahuWindowState => {
  if (typeof window === 'undefined') {
    return {
      isDocked: false,
      size: { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
    };
  }

  try {
    const storedState = localStorage.getItem(STORAGE_KEY);
    if (storedState) {
      const parsed = JSON.parse(storedState);
      return {
        isDocked: parsed.isDocked ?? false,
        size: parsed.size ?? { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
      };
    }
  } catch (e) {
    // ignore parsing errors
  }

  return {
    isDocked: false,
    size: { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
  };
};

export const useKahuDockState = () => {
  const [state, setState] = useState<KahuWindowState>(getInitialState);

  const { isDocked, size } = state;

  // Always holds the latest committed state so updateState never reads stale
  // values from a closure. Updated synchronously on every render.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    document.body.setAttribute('data-kahu-docked', String(isDocked));
  }, [isDocked]);

  useEffect(() => {
    const sync = () => setState(getInitialState());

    sync();

    window.addEventListener('storage', sync); // cross tab
    window.addEventListener(KAHU_WINDOW_STATE_EVENT, sync); // same tab
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(KAHU_WINDOW_STATE_EVENT, sync);
    };
  }, []);

  // Writes localStorage synchronously BEFORE dispatching the event so that the
  // cross-tab sync handler always reads fresh data (not stale pre-update data).
  const updateState = useCallback((partial: Partial<KahuWindowState>) => {
    const next = { ...stateRef.current, ...partial };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setState(next);
    window.dispatchEvent(new Event(KAHU_WINDOW_STATE_EVENT));
  }, []);

  const setDocked = useCallback(
    (docked: boolean) => {
      updateState({ isDocked: docked });
      document.body.setAttribute('data-kahu-docked', String(docked));
    },
    [updateState]
  );

  const toggleDocked = useCallback(() => {
    const goingDocked = !stateRef.current.isDocked;

    const next = {
      ...stateRef.current,
      isDocked: goingDocked,
      // Force fixed width of 378px when docking, restore nothing when undocking
      ...(goingDocked
        ? { size: { ...stateRef.current.size, width: DEFAULT_WIDTH } }
        : {}),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    document.body.setAttribute('data-kahu-docked', String(next.isDocked));
    setState(next);
    window.dispatchEvent(new Event(KAHU_WINDOW_STATE_EVENT));
  }, []);

  const setSize = useCallback(
    (newSize: { width: number; height: number }) => {
      updateState({ size: newSize });
    },
    [updateState]
  );

  return useMemo(
    () =>
      ({
        isDocked,
        setDocked,
        toggleDocked,
        size,
        setSize,
      }) as const,
    [isDocked, setDocked, toggleDocked, size, setSize]
  );
};
